import { Trash, X } from '@griply/assets';
import React, { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTasksList } from 'shared/components/connected/sub-tasks-list';
import { TaskCalendarSyncButton } from 'shared/components/connected/task-calendar-sync-button';
import { ConfirmDialog } from 'shared/components/ui/confirm-dialog';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { Habit } from 'shared/types/habit';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { RepeatingTask } from 'shared/types/repeating-task';
import { Task, UpdateTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { Main } from './components/detail-main/main';
import { QuickActions } from './components/quick-actions';
import { TaskDescription } from './components/task-description';
import { Meta } from './meta';
import * as Styled from './task-detail.style';
import { MetaViewOptions } from './types';

export type TaskDetailProps = {
  onDelete: (task: Task) => void;
  onDeleteHabit: (habit: Habit | RepeatingTask, redirectPath?: string) => void;
  onUpdate: (task: UpdateTask) => void;
  onComplete: (
    task: Task,
    type?: ScheduleEntryState,
    options?: {
      value?: number;
      increment?: number;
      referenceDate?: Timestamp;
    },
  ) => void;
  onUpdateRelationships: (relationTypeIds: ID[]) => void;
  onClose: () => void;
  task: Task;
  openOnView?: MetaViewOptions;
  noBreakpoints?: boolean;
};

export const TaskDetail: React.FC<TaskDetailProps> = ({
  onDelete,
  onDeleteHabit,
  onUpdate,
  onComplete,
  onUpdateRelationships,
  onClose,
  task,
  openOnView,
  noBreakpoints,
}) => {
  const { t } = useTranslation();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedView, setSelectedView] = useState<MetaViewOptions>(
    !openOnView ||
      // cannot open non-settings views for non-habit tasks, so fallback on settings
      (task?.type !== TaskType.Habit && openOnView !== MetaViewOptions.Settings)
      ? MetaViewOptions.Settings
      : openOnView,
  );

  const isHabitOrRepeating = [TaskType.Habit, TaskType.Repeating].includes(
    task.type,
  );

  const _archiveTask = () => {
    onUpdate({
      id: task.id,
      archivedAt: task.archivedAt ? null : new Date(),
    });
    onClose();
  };

  // Habits get their own delete dialog upstream (useOpenDeleteHabitDialog), so
  // only the plain-task path needs a confirmation here. Deliberately detail-view-
  // only: deleting from a task card's context menu stays instant.
  const _deleteTask = () => {
    if ([TaskType.Habit, TaskType.Repeating].includes(task.type)) {
      onDeleteHabit(task as Habit);
      onClose();
      return;
    }

    setDeleteConfirmOpen(true);
  };

  const cancelDeleteTask = () => setDeleteConfirmOpen(false);

  const confirmDeleteTask = () => {
    setDeleteConfirmOpen(false);
    onDelete(task);
    onClose();
  };

  const _updateTask = (taskUpdate: Partial<Task>) =>
    onUpdate({ ...taskUpdate, id: task.id });

  const _updateTagRelationships = (relationshipIds: ID[]) =>
    onUpdateRelationships(relationshipIds);

  const updateDescription = (description: string) =>
    _updateTask({ description });

  const updateAndSubmit = (task: Partial<Task>) => {
    if (!task.name) {
      return;
    }

    _updateTask(task);

    onClose();
  };

  const _onComplete = (date?: Timestamp) =>
    onComplete(task, undefined, { referenceDate: date });
  const _onClose = () => onClose();

  return (
    <Styled.Container>
      <Styled.Body $noBreakpoints={!!noBreakpoints}>
        <Styled.Main>
          <Styled.Header>
            <IconButton
              icon={X}
              color={IconButtonColors.Default}
              size={IconButtonSizes.Medium}
              strokeWidth={2}
              onClick={_onClose}
            />
            <Styled.HeaderButtons>
              <TaskCalendarSyncButton task={task} />
              {task.type === TaskType.Task && (
                <IconButton
                  icon={Trash}
                  color={IconButtonColors.Default}
                  size={IconButtonSizes.Medium}
                  strokeWidth={1.5}
                  onClick={_deleteTask}
                />
              )}
            </Styled.HeaderButtons>
          </Styled.Header>

          <Styled.MainBody>
            <Main
              task={task}
              onComplete={_onComplete}
              updateTask={_updateTask}
              onNameSubmit={updateAndSubmit}
            />
            {isHabitOrRepeating && (
              <QuickActions onComplete={onComplete} task={task} />
            )}
            <Styled.BodyContent>
              <Styled.DescriptionContainer>
                <TaskDescription
                  description={task.description}
                  updateDescription={updateDescription}
                />
              </Styled.DescriptionContainer>

              <Suspense>
                <Styled.SubTaskListContainer>
                  <SubTasksList task={task as Task} />
                </Styled.SubTaskListContainer>
              </Suspense>
            </Styled.BodyContent>
          </Styled.MainBody>
        </Styled.Main>

        <Styled.SidePanel
          $wide={task.type !== TaskType.Task}
          $noBreakpoints={!!noBreakpoints}
        >
          <Meta
            task={task}
            updateTask={_updateTask}
            updateRelationships={_updateTagRelationships}
            view={selectedView}
            updateView={setSelectedView}
            onDelete={_deleteTask}
            onArchive={_archiveTask}
          />
        </Styled.SidePanel>
      </Styled.Body>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={t('tasks.delete-confirm-title', { count: 1 })}
        message={t('tasks.delete-confirm-message', { count: 1 })}
        onConfirm={confirmDeleteTask}
        onCancel={cancelDeleteTask}
      />
    </Styled.Container>
  );
};
